/**
 * Menu that is triggered from the site header and shows profile-level nav items
 */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withStyles } from '@material-ui/styles';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Icon from '@material-ui/core/Icon';
import Divider from '@material-ui/core/Divider';
import { NavLink } from 'react-router-dom';

import { lightTheme } from 'components/App/App.theme';
import SegmentClickTracker from 'components/SegmentClickTracker/SegmentClickTrackerContainer';
import WithSpecifiedTheme from 'components/WithSpecifiedTheme/WithSpecifiedTheme';

const propTypes = {
  className: PropTypes.string.isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    selectedLink: PropTypes.string.isRequired,
    menuItem: PropTypes.string.isRequired,
  }).isRequired,
  anchorEl: PropTypes.oneOfType([PropTypes.object]),
  onClose: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  isORv4Admin: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    email: PropTypes.string.isRequired,
  }).isRequired,
};

const defaultProps = {
  anchorEl: null,
};

export function UnstyledMenu({ className, classes, anchorEl, onClose, isAdmin, isORv4Admin, user }) {
  return (
    <Menu
      className={classnames(className, classes.root)}
      open={!!anchorEl}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'right', vertical: 50 }}
      disableAutoFocusItem
      getContentAnchorEl={null}
    >
      <SegmentClickTracker buttonName="Profile Management (Menu)">
        <MenuItem
          className={classnames(classes.menuItem)}
          component={NavLink}
          to="/mission-ctrl/profile"
          onClick={onClose}
          classes={{ selected: classes.selectedLink }}
        >
          <span className={classes.profileTextContainer}>
            Profile Management
            <br />
            <span className={classes.email}>{user.email}</span>
          </span>
        </MenuItem>
      </SegmentClickTracker>

      {/* Only users with Admin, Owner, or 'OR Admin' roles can access V4 */}
      {isORv4Admin && (
        <SegmentClickTracker buttonName="ORv4 SSO (Menu)">
          <MenuItem
            className={classnames(classes.menuItem)}
            component="a"
            href={process.env.REACT_APP_ORV4_SSO_LOGIN}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            classes={{ selected: classes.selectedLink }}
          >
            <span className={classes.orv4link}>app.objectrocket.com</span>
            <Icon className={classes.linkIcon}>open_in_new</Icon>
          </MenuItem>
        </SegmentClickTracker>
      )}

      {isAdmin && (
        <SegmentClickTracker buttonName="Impersonate (Menu)">
          <MenuItem
            className={classes.menuItem}
            component={NavLink}
            to="/mission-ctrl/org-impersonation"
            onClick={onClose}
            classes={{ selected: classes.selectedLink }}
          >
            Impersonate an Org
          </MenuItem>
        </SegmentClickTracker>
      )}
      <Divider />
      <SegmentClickTracker buttonName="Log Out (Menu)">
        <MenuItem
          className={classes.menuItem}
          component={NavLink}
          to="/logout"
          onClick={onClose}
          classes={{ selected: classes.selectedLink }}
        >
          Log Out
        </MenuItem>
      </SegmentClickTracker>
    </Menu>
  );
}

UnstyledMenu.propTypes = propTypes;
UnstyledMenu.defaultProps = defaultProps;

const styles = (theme) => ({
  root: {
    padding: 0,
    marginLeft: -15,
  },
  selectedLink: {
    backgroundColor: theme.palette.action.selected,
  },
  menuItem: {
    padding: theme.spacing(1),
  },
  orv4link: {
    color: 'blue',
  },
  linkIcon: {
    marginLeft: '2rem',
  },
  profileTextContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  email: {
    fontSize: '14px',
    color: theme.customColors.mediumGrey,
  },
});

export default WithSpecifiedTheme(lightTheme)(withStyles(styles)(UnstyledMenu));
